/**
 * HTTP layer for the split World Cup matches endpoints.
 *
 * The server exposes three routes that all return the same match data sliced
 * differently, chosen to keep the Redis-backed static payload shared across
 * users while the tiny per-user overlay stays fresh:
 *
 *   GET /competitions/:id/matches/static  user-agnostic, Redis-cached (~30s).
 *                                         Safe for thousands of concurrent
 *                                         clients at kickoff.
 *   GET /competitions/:id/matches/me      per-user overlay (picks, golden
 *                                         balls, futures). Small, always
 *                                         fresh, indexed reads.
 *   GET /competitions/:id/matches         combined legacy shape — kept on the
 *                                         server for backwards compatibility,
 *                                         no longer called from this client.
 *
 * Clients fetch the two halves in parallel and merge them into the existing
 * shape so downstream hooks (useWorldCupPicksData, useWorldCupPickPersistence)
 * stay unchanged.
 */

import type { AxiosRequestConfig, AxiosResponse } from 'axios';
import type {
  ApiMatch,
  CompetitionMatchesApiPayload,
  WorldCupFuturesOptions,
} from './worldcupPicksData.helper';
import type { FuturesPick } from '../hooks/useWorldCupPicksData';

type ApiClientFn = <T = unknown>(
  endpoint: string,
  options?: AxiosRequestConfig,
) => Promise<AxiosResponse<T>>;

// ---------------------------------------------------------------------------
// Server response shapes — one type per endpoint.
// ---------------------------------------------------------------------------

/** Per-match fields that are identical for every user — safe to share in Redis. */
export interface StaticMatch {
  competitionMatchId: string;
  matchId: string;
  homeTeamId: string;
  awayTeamId: string;
  homeTeam: string;
  awayTeam: string;
  homeCountryCode: string;
  awayCountryCode: string;
  kickoffTime: string;
  status: 'upcoming' | 'live' | 'finished';
  stage: string;
  stageLabel: string;
  round: string | null;
  displayOrder: number;
  homeScore: number | null;
  awayScore: number | null;
  /** 90-minute (regular-time) score; only present for finished matches (absent on older cached payloads). */
  fullTimeHomeScore?: number | null;
  fullTimeAwayScore?: number | null;
  /** Penalty-shootout score; only present when a finished match was decided on penalties. */
  penaltyHomeScore?: number | null;
  penaltyAwayScore?: number | null;
  liveMinute: number | null;
  isPickEditable: boolean;
}

export interface StaticMatchesPayload {
  competition: { id: string; name: string };
  matches: StaticMatch[];
  futuresOptions?: WorldCupFuturesOptions;
  futuresEditable?: boolean;
}

/** The signed-in user's picks, golden-ball assignments, and futures. */
export interface UserMatchesOverlayPayload {
  picks: Record<string, {
    home: number;
    away: number;
    isMonkeyPick?: boolean;
    pointsEarned: number | null;
  }>;
  goldenBalls: Record<string, { competitionMatchId: string | null; locked: boolean }>;
  /** Per-round Golden Ball assignments keyed by competitionMatchId (Group Stage = up to 3). */
  goldenBallsByMatch?: Record<string, { competitionMatchId: string; locked: boolean }>;
  futures: FuturesPick | null;
  futuresEditable?: boolean;
}

interface ServerEnvelope<T> {
  success: boolean;
  error?: string;
  data: T;
}

// ---------------------------------------------------------------------------
// Fetchers — pure functions, easy to test, obvious at the call site.
// ---------------------------------------------------------------------------

async function unwrapEnvelope<T>(
  response: AxiosResponse<ServerEnvelope<T>>,
  fallbackError: string,
): Promise<T> {
  const body = response.data;
  if (!body?.success) {
    throw new Error(body?.error || fallbackError);
  }
  return body.data;
}

/** Fetch the user-agnostic competition payload (matches, teams, futures options). */
export async function fetchWorldCupStaticMatches(
  apiClient: ApiClientFn,
  competitionId: string,
): Promise<StaticMatchesPayload> {
  const response = await apiClient<ServerEnvelope<StaticMatchesPayload>>(
    `/competitions/${competitionId}/matches/static`,
  );
  return unwrapEnvelope(response, 'Failed to fetch World Cup static matches');
}

/** Fetch the signed-in user's picks / golden balls / futures overlay. */
export async function fetchWorldCupUserMatchesOverlay(
  apiClient: ApiClientFn,
  competitionId: string,
): Promise<UserMatchesOverlayPayload> {
  const response = await apiClient<ServerEnvelope<UserMatchesOverlayPayload>>(
    `/competitions/${competitionId}/matches/me`,
  );
  return unwrapEnvelope(response, 'Failed to fetch World Cup user matches overlay');
}

/** Server response for the "autofill picks" (reveal monkey picks) POST endpoint. */
export interface RevealMonkeyPicksResult {
  revealedMatchIds: string[];
  count: number;
}

/**
 * Adopt the signed-in user's still-editable monkey match-score picks in bulk.
 * Returns the matchIds that were revealed so the caller can patch the overlay cache.
 */
export async function revealWorldCupMonkeyPicks(
  apiClient: ApiClientFn,
  competitionId: string,
): Promise<RevealMonkeyPicksResult> {
  const response = await apiClient<ServerEnvelope<RevealMonkeyPicksResult>>(
    `/competitions/${competitionId}/picks/reveal-monkey`,
    { method: 'POST' },
  );
  return unwrapEnvelope(response, 'Failed to autofill World Cup picks');
}

// ---------------------------------------------------------------------------
// Merge — produce the legacy combined shape that existing hooks consume.
// ---------------------------------------------------------------------------

/**
 * Combine the static payload with the user overlay into the shape existing
 * consumers (useWorldCupPicksData, cache-writing mutations) already expect.
 */
export function mergeWorldCupMatchesPayload(
  staticPayload: StaticMatchesPayload,
  overlay: UserMatchesOverlayPayload,
): CompetitionMatchesApiPayload['data'] {
  const goldenBallByMatch = overlay.goldenBallsByMatch ?? {};
  const matches: ApiMatch[] = staticPayload.matches.map((match) => {
    const pick = overlay.picks[match.matchId];
    const matchGoldenBall = goldenBallByMatch[match.competitionMatchId];
    const hasGoldenBall = Boolean(matchGoldenBall);
    const isGoldenBallLocked = hasGoldenBall && Boolean(matchGoldenBall?.locked);

    return {
      ...match,
      prediction: pick
        ? {
            home: pick.home,
            away: pick.away,
            isMonkeyPick: pick.isMonkeyPick,
            pointsEarned: pick.pointsEarned,
          }
        : null,
      hasGoldenBall,
      isGoldenBallLocked,
    };
  });

  return {
    matches,
    futuresOptions: staticPayload.futuresOptions,
    goldenBalls: overlay.goldenBalls,
    goldenBallsByMatch: overlay.goldenBallsByMatch,
    futures: overlay.futures,
    futuresEditable: overlay.futuresEditable ?? staticPayload.futuresEditable,
  };
}
